import { TopNav } from "@dashboard/components/AppLayout/TopNav";
import { DashboardCard } from "@dashboard/components/Card";
import { ListPageLayout } from "@dashboard/components/Layouts";
import ResponsiveTable from "@dashboard/components/ResponsiveTable";
import TableRowLink from "@dashboard/components/TableRowLink";
import { CustomerFormsTabs } from "@dashboard/customerForms/CustomerFormsTabs";
import { customerUrl } from "@dashboard/customers/urls";
import { useProductAlertsQuery } from "@dashboard/graphql";
import useNotifier from "@dashboard/hooks/useNotifier";
import { productUrl } from "@dashboard/products/urls";
import { TableBody, TableCell, TableHead } from "@material-ui/core";
import { Box, Button, Skeleton, Text } from "@saleor/macaw-ui-next";
import { useState } from "react";
import { useIntl } from "react-intl";
import { Link } from "react-router-dom";

import { productAlertsMessages as m } from "./messages";

const numberOfColumns = 3;
// Subscriber e-mails shown before "Show all".
const PREVIEW_COUNT = 5;

export const ProductAlertsList = () => {
  const intl = useIntl();
  const notify = useNotifier();
  const { data, loading } = useProductAlertsQuery({ fetchPolicy: "cache-and-network" });
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const alerts = data?.productAlerts;

  const copyEmails = async (emails: string[]) => {
    await navigator.clipboard.writeText(emails.join(", "));
    notify({ status: "success", text: intl.formatMessage(m.copied) });
  };

  return (
    <ListPageLayout>
      <TopNav title={intl.formatMessage(m.title)} withoutBorder isAlignToRight={false} />
      <Box display="flex" flexDirection="column">
        <CustomerFormsTabs active="productAlerts" />
        <DashboardCard>
          <DashboardCard.Header>
            <DashboardCard.Subtitle>{intl.formatMessage(m.subtitle)}</DashboardCard.Subtitle>
          </DashboardCard.Header>
          <ResponsiveTable data-test-id="product-alerts-list">
            <TableHead>
              <TableRowLink>
                <TableCell>{intl.formatMessage(m.colProduct)}</TableCell>
                <TableCell>{intl.formatMessage(m.colCount)}</TableCell>
                <TableCell>{intl.formatMessage(m.colEmails)}</TableCell>
              </TableRowLink>
            </TableHead>
            <TableBody>
              {alerts === undefined && loading ? (
                <TableRowLink>
                  <TableCell colSpan={numberOfColumns}>
                    <Skeleton />
                  </TableCell>
                </TableRowLink>
              ) : !alerts?.length ? (
                <TableRowLink data-test-id="empty-list-message">
                  <TableCell colSpan={numberOfColumns}>{intl.formatMessage(m.empty)}</TableCell>
                </TableRowLink>
              ) : (
                alerts.map(alert => {
                  const isExpanded = expanded[alert.productSlug];
                  const shown = isExpanded
                    ? alert.subscribers
                    : alert.subscribers.slice(0, PREVIEW_COUNT);

                  return (
                    <TableRowLink
                      key={alert.productSlug}
                      data-test-id={"alert-" + alert.productSlug}
                    >
                      <TableCell style={{ verticalAlign: "top" }}>
                        {alert.productId ? (
                          <Link to={productUrl(alert.productId)}>
                            <Text fontWeight="medium">{alert.productName}</Text>
                          </Link>
                        ) : (
                          <Text color="default2">
                            {intl.formatMessage(m.removedProduct, { slug: alert.productSlug })}
                          </Text>
                        )}
                      </TableCell>
                      <TableCell style={{ verticalAlign: "top" }}>
                        <Text fontWeight="bold">{alert.subscriberCount}</Text>
                      </TableCell>
                      <TableCell style={{ verticalAlign: "top" }}>
                        <Box display="flex" flexDirection="column" gap={1}>
                          {shown.map(subscriber => (
                            <Link
                              key={subscriber.customerId}
                              to={customerUrl(subscriber.customerId)}
                            >
                              <Text size={2}>{subscriber.email}</Text>
                            </Link>
                          ))}
                          <Box display="flex" gap={2} paddingTop={1}>
                            {!isExpanded && alert.subscribers.length > PREVIEW_COUNT && (
                              <Button
                                size="small"
                                variant="tertiary"
                                onClick={() =>
                                  setExpanded(prev => ({ ...prev, [alert.productSlug]: true }))
                                }
                              >
                                {intl.formatMessage(m.showAll, { count: alert.subscriberCount })}
                              </Button>
                            )}
                            <Button
                              size="small"
                              variant="secondary"
                              onClick={() => copyEmails(alert.subscribers.map(s => s.email))}
                            >
                              {intl.formatMessage(m.copyEmails)}
                            </Button>
                          </Box>
                        </Box>
                      </TableCell>
                    </TableRowLink>
                  );
                })
              )}
            </TableBody>
          </ResponsiveTable>
        </DashboardCard>
      </Box>
    </ListPageLayout>
  );
};
